import h from 'hyperscript';

import type { Teardown } from '@disy/lib/teardown';
import { tearDown } from '@disy/lib/teardown';

import { getStore } from 'cadenza/store/cadenza-store';
import type { ViewId } from 'cadenza/workbook/api/view-id';
import { toDataViewId } from 'cadenza/workbook/model/data-view-id-utils';
import type {
  DataViewNotification,
  LayerNotification,
  NotificationLevel,
  WorkbookNotification,
} from 'cadenza/workbook/notifications/workbook-notifications';
import {
  getChartDataViewNameWithSuffixIfApplicable,
  hasReferenceLine,
} from 'cadenza/workbook/state/workbook-chart-view-selectors.state';
import { createDetailLinkElement } from 'cadenza/workbook/view/view-header/view-notification-bar-details';
import { getLayerName, getViewById } from 'cadenza/workbook/workbook-core-selectors.state';

import './view-notification-bar.css';
import i18n from './view-notification-bar.properties';

const COMPONENT_NAME = 'd-view-notification-bar';

type NotificationLevelSupportedInViewNotification = Extract<NotificationLevel, 'INFO' | 'WARNING' | 'ERROR'>;

export interface CollapsedNotification {
  notification: WorkbookNotification;
  count: number;
}

const SUPPORTED_LEVELS: NotificationLevelSupportedInViewNotification[] = ['ERROR', 'WARNING', 'INFO'];

/**
 * Bar shown in the view header that displays notifications like non-fatal errors and warnings to
 * the user in a prominent way.
 *
 * Only warnings and errors are relevant for this component. Info level notifications are handled
 * only in the specific UI level, such as layer in the view designer.
 *
 * Fatal error cause the whole view to not be rendered and a prominent error message is shown instead.
 *
 * Notifications come from the Redux store, but this is a presentation component that is only
 * responsible for rendering the notifications.
 * The notifications are provided and updated by the parent element, which is the view.
 */
export class ViewNotificationBar extends HTMLElement {
  readonly #viewId: ViewId;
  #teardowns: Teardown[] = [];
  #notifications: Record<NotificationLevelSupportedInViewNotification, CollapsedNotification[]> = {
    ERROR: [],
    WARNING: [],
    INFO: [],
  };

  constructor(viewId: ViewId) {
    super();
    this.#viewId = viewId;
  }

  connectedCallback() {
    this.classList.add(COMPONENT_NAME, 'd-stack-h', 'space-1', 'd-alert', 'd-alert-small');
    this.hidden = true;
  }

  disconnectedCallback() {
    this.#teardowns = tearDown(this.#teardowns);
  }

  update(notifications: WorkbookNotification[] | undefined) {
    notifications = notifications ?? [];
    const hasFatalError = notifications.some((notification) => notification.level === 'FATAL');

    // If the view has a fatal error, e.g. because of missing data,
    // the view notification bar is redundant and should not be shown.
    if (hasFatalError) {
      notifications = [];
      this._render();
    }

    SUPPORTED_LEVELS.forEach((level) => {
      this.#notifications[level] = [];
    });

    const collapsedNotifications = this._getCollapsedNotifications(notifications);

    // we only show errors and warnings, fatal errors are shown inside the view and info are not shown here.
    collapsedNotifications.forEach(({ notification, count }) => {
      if (isSupportedInViewNotificationBar(notification.level)) {
        this.#notifications[notification.level].push({ notification, count });
      }
    });

    this._render();
  }

  _getCollapsedNotifications(notifications: WorkbookNotification[]) {
    const collapsedNotifications = notifications.reduce((acc: Record<string, CollapsedNotification>, curr) => {
      const layerId = (curr as LayerNotification).layerId ?? (curr as DataViewNotification).dataViewId;
      const key = `${curr.title}-${curr.detail}-${curr.level}-${layerId}-${curr.additionalGroupingKey}`;
      if (!acc[key]) {
        acc[key] = { notification: curr, count: 1 };
      } else {
        acc[key].count++;
      }
      return acc;
    }, {});
    return Object.values(collapsedNotifications);
  }

  /**
   * Re-renders the component by updating it to its model state.
   */
  _render() {
    const collapsedNotifications = SUPPORTED_LEVELS.flatMap((level) => this.#notifications[level]);
    const notifications = collapsedNotifications.map((el) => el.notification);

    this.innerHTML = '';
    this.hidden = notifications.length === 0;

    if (!this.hidden) {
      const [firstNotification] = notifications;
      const highestSeverity = firstNotification.level as NotificationLevelSupportedInViewNotification; // we know it can only be ERROR or WARNING;
      const notificationCount = this.#notifications[highestSeverity].reduce((acc, curr) => {
        acc += curr.count;
        return acc;
      }, 0);
      this.classList.toggle('d-alert-error', highestSeverity === 'ERROR');
      this.classList.toggle('d-alert-warning', highestSeverity === 'WARNING');
      this.classList.toggle('d-alert-info', highestSeverity === 'INFO');

      this.append(
        h(
          `span.${COMPONENT_NAME}--title.ellipsis`,
          notifications.length === 1 ?
            this._createSingleNotificationMessage(firstNotification, highestSeverity)
          : i18n(`<${highestSeverity}>`, { count: notificationCount }),
        ),
      );

      if (this.#showDetailLink(collapsedNotifications)) {
        this.append(createDetailLinkElement(this.#viewId, collapsedNotifications));
      }
    }
  }

  #showDetailLink(collapsedNotifications: CollapsedNotification[]) {
    return collapsedNotifications.some((notification) => notification.notification.detail);
  }

  _createSingleNotificationMessage(
    notification: WorkbookNotification,
    highestSeverity: NotificationLevelSupportedInViewNotification,
  ) {
    const layerId = (notification as LayerNotification).layerId ?? (notification as DataViewNotification).dataViewId;
    const message = notification.title ?? i18n(`<${highestSeverity}>`, { count: 1 });
    if (layerId) {
      if (getViewById(getStore().getState(), this.#viewId).type === 'MAP') {
        return `${getLayerName(getStore().getState(), this.#viewId, layerId)}: ${message}`;
      } else if (hasReferenceLine(getStore().getState(), this.#viewId)) {
        return `${getChartDataViewNameWithSuffixIfApplicable(getStore().getState(), toDataViewId(layerId))}: ${message}`;
      }
    }
    return message;
  }

  /**
   * All error notifications shown by the bar.
   *
   * @return All notifications of type error
   */
  get errors() {
    return [...this.#notifications['ERROR']];
  }

  /**
   * All warning notifications shown by the bar.
   *
   * @return All notifications of type warning
   */
  get warnings() {
    return [...this.#notifications['WARNING']];
  }

  /**
   * All info notifications shown by the bar.
   *
   * @return All notifications of type info
   */
  get infos() {
    return [...this.#notifications['INFO']];
  }
}

customElements.define(COMPONENT_NAME, ViewNotificationBar);

function isSupportedInViewNotificationBar(
  notificationLevel: NotificationLevel,
): notificationLevel is NotificationLevelSupportedInViewNotification {
  return notificationLevel === 'ERROR' || notificationLevel === 'WARNING' || notificationLevel === 'INFO';
}
